// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, first, uniqBy } from 'lodash/fp';

// Util
import { isPlatformIframe } from '../../../platform/utils/platformDetailsUtils';

// Components
import Breadcrumb from './Breadcrumb';
import BreadcrumbTruncateUtil from './breadcrumbTruncateUtil/BreadcrumbTruncateUtil';
import EllipsisAnimation from '../../../components/animations/EllipsisAnimation';

// Selectors
import breadCrumbListSelector from './breadcrumbListSelector';

// Styles
import './BreadcrumbList.scss';

const TRUNCATE_POINT_TYPE = 'TRUNCATE_POINT';

const getSize = (breadcrumb) => get('size', breadcrumb) || 0;
const uniqByElementId = uniqBy('elementId');

const getRenderedBreadcrumbs = ({ rootBoardId, isMobileView, breadcrumbPath, availableBreadcrumbsWidth }) => {
    // in mobile view, just return the second last breadcrumb in the stack
    if (isMobileView) {
        const breadcrumb = breadcrumbPath.slice(-2, -1);

        if (breadcrumb.length > 0) return breadcrumb;

        return rootBoardId
            ? [{ elementId: rootBoardId, title: 'Home', isRoot: true }]
            : [{ elementId: 'login', title: 'Login', isRoot: true }];
    }

    // if no availableBreadcrumbsWidth, just return the breadcrumbs, no need to work out how many can fit
    if (!availableBreadcrumbsWidth) return breadcrumbPath;

    let totalSize = getSize(first(breadcrumbPath));
    let lastVisibleIndex = 0;

    for (let i = breadcrumbPath.length - 1; i > 0; i--) {
        const breadcrumbEntry = breadcrumbPath[i];

        totalSize = totalSize + getSize(breadcrumbEntry);

        if (totalSize > availableBreadcrumbsWidth) {
            lastVisibleIndex = i + 1;
            break;
        }
    }

    const truncatePoint = breadcrumbPath.length - lastVisibleIndex;

    if (truncatePoint <= 0) {
        return [
            ...breadcrumbPath.slice(0, 1),
            {
                specialType: TRUNCATE_POINT_TYPE,
            },
        ];
    }

    if (truncatePoint < breadcrumbPath.length) {
        return uniqByElementId([
            ...breadcrumbPath.slice(0, 1),
            {
                specialType: TRUNCATE_POINT_TYPE,
            },
            ...breadcrumbPath.slice(truncatePoint * -1),
        ]);
    }

    return breadcrumbPath;
};

const BreadcrumbList = ({
    isMobileView,
    breadcrumbPath,
    breadcrumbObj,
    currentBoardId,
    rootBoardId,
    detachedBreadcrumbIsValid,
    availableBreadcrumbsWidth,
    isLoading,
    isSyncing,
    isDarkModeTheme,
    platformDetails,
}) => {
    // If we currently haven't loaded all our aliases then we don't truly know
    // if the board is detached, so just show the loader.
    if (isLoading || (breadcrumbPath.length === 0 && !isPlatformIframe(platformDetails))) {
        return (
            <div className="BreadcrumbList">
                <ul>
                    <li key={rootBoardId}>
                        <Breadcrumb
                            elementId={rootBoardId || 'root'}
                            currentBoardId={currentBoardId}
                            index={0}
                            title="Home"
                            isLast
                            isLoading
                            isDarkModeTheme={isDarkModeTheme}
                        />
                    </li>
                    <li key="loading">
                        <Breadcrumb
                            elementId="loading"
                            currentBoardId="loading"
                            index={1}
                            title={
                                <span>
                                    Loading
                                    <EllipsisAnimation />
                                </span>
                            }
                            isLast={rootBoardId === currentBoardId}
                            isDarkModeTheme={isDarkModeTheme}
                        />
                    </li>
                </ul>
            </div>
        );
    }

    const renderedBreadcrumbs = getRenderedBreadcrumbs({
        rootBoardId,
        isMobileView,
        breadcrumbPath,
        availableBreadcrumbsWidth,
    });

    // for iframes we dont to show the breadcrumb list if we are on the top level board
    if (isPlatformIframe(platformDetails) && breadcrumbObj.path.length < 2) return null;

    return (
        <div className="BreadcrumbList">
            <ul>
                {renderedBreadcrumbs.map(({ specialType, elementId, title, isRoot, color }, index) => {
                    if (specialType === TRUNCATE_POINT_TYPE) {
                        return (
                            <li key={TRUNCATE_POINT_TYPE}>
                                <BreadcrumbTruncateUtil
                                    currentBoardId={currentBoardId}
                                    breadcrumbPath={breadcrumbPath}
                                    renderedBreadcrumbs={renderedBreadcrumbs}
                                />
                            </li>
                        );
                    }

                    const prevBreadcrumb = renderedBreadcrumbs[index - 1];
                    const isAfterTruncate = prevBreadcrumb?.specialType === TRUNCATE_POINT_TYPE;

                    return (
                        <li key={elementId || 'root'}>
                            <Breadcrumb
                                elementId={elementId}
                                currentBoardId={currentBoardId}
                                title={title}
                                isRoot={isRoot}
                                index={index}
                                isLast={index === renderedBreadcrumbs.length - 1}
                                // Show the spinner on the root board icon if we're syncing
                                isLoading={isRoot && isSyncing}
                                isAfterTruncate={isAfterTruncate}
                                isDarkModeTheme={isDarkModeTheme}
                            />
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

BreadcrumbList.propTypes = {
    rootBoardId: PropTypes.string,
    breadcrumbPath: PropTypes.array,
    currentBoardId: PropTypes.string,
    detachedBreadcrumbIsValid: PropTypes.bool,
    isLoading: PropTypes.bool,
    isSyncing: PropTypes.bool,
    isDarkModeTheme: PropTypes.bool,
    isMobileView: PropTypes.bool,
    availableBreadcrumbsWidth: PropTypes.number,
    breadcrumbObj: PropTypes.object,
    platformDetails: PropTypes.object,
};

export default connect(breadCrumbListSelector)(BreadcrumbList);
